import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderOpen,
  MessageSquare,
  History as HistoryIcon,
  FileText,
  LogOut,
  User,
  Menu,
  X,
  Activity,
  Settings
} from 'lucide-react';
import ProxySettings from './ProxySettings';
import DashboardHome from './DashboardHome';
import Projects from './Projects';
import Requests from './Requests';
import History from './History';
import Results from './Results';
import Reports from './Reports';
import SystemStatus from './SystemStatus';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Закрываем меню при изменении маршрута
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Закрываем меню при изменении размера экрана
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Блокируем скролл body при открытом меню
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Дашборд', exact: true },
    { path: '/dashboard/projects', icon: FolderOpen, label: 'Проекты' },
    { path: '/dashboard/requests', icon: MessageSquare, label: 'Запросы' },
    { path: '/dashboard/results', icon: FileText, label: 'Результаты' },
    { path: '/dashboard/history', icon: HistoryIcon, label: 'История задач' },
    { path: '/dashboard/reports', icon: FileText, label: 'Отчёты' },
    { path: '/dashboard/status', icon: Activity, label: 'Статус системы' },
    { path: '/dashboard/proxy-settings', icon: Settings, label: 'Настройки прокси' }
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="dashboard">
      {/* Мобильный header */}
      <header className="mobile-header">
        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h2 className="mobile-title">Личный кабинет</h2>
        <div className="mobile-user-avatar">
          <User size={18} />
        </div>
      </header>

      {/* Overlay для мобильного меню */}
      <div
        className={`sidebar-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      <nav className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Личный кабинет</h2>
          <button className="sidebar-close-btn" onClick={() => setIsMobileMenuOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            <User size={20} />
          </div>
          <span className="user-name">{user?.username}</span>
        </div>

        <ul className="sidebar-menu">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`menu-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <IconComponent size={20} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={20} />
            <span>Выйти</span>
          </button>
        </div>
      </nav>

      <main className="main-content">
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="projects" element={<Projects />} />
          <Route path="requests" element={<Requests />} />
          <Route path="results" element={<Results />} />
          <Route path="history" element={<History />} />
          <Route path="reports" element={<Reports />} />
          <Route path="status" element={<SystemStatus />} />
          <Route path="proxy-settings" element={<ProxySettings />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;
